import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ChevronLeft, Download, ExternalLink, Upload, Eye, CheckCircle2 } from "lucide-react";
import {
  getTaskDetail,
  setTargetStatus,
  addTarget,
  removeTarget,
  setFulfillment,
  uploadTargetProof,
  getTargetProofUrl,
  completeTask,
  getCompletionReportUrl,
  resendCompletionEmail,
} from "@/lib/admin.functions";
import { getOnePagerPdf } from "@/lib/onepager.functions";

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DeckAnalysisView, ScoreHeader } from "@/components/deck-analysis-view";

export const Route = createFileRoute("/tasks/$submissionId")({
  head: () => ({
    meta: [
      { title: "Task - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TaskDetail,
});

type Detail = Awaited<ReturnType<typeof getTaskDetail>>;
type TargetRow = Detail["targets"][number];

type FormShape = Record<string, unknown> | null;

const FORM_FIELDS: Array<{ key: string; label: string }> = [
  { key: "company", label: "Company" },
  { key: "elevatorPitch", label: "Elevator pitch" },
  { key: "stage", label: "Stage" },
  { key: "raising", label: "Raising" },
  { key: "valuation", label: "Valuation" },
  { key: "vcBacked", label: "VC-backed" },
  { key: "mrr", label: "MRR" },
  { key: "revenueModel", label: "Revenue model" },
  { key: "location", label: "Location" },
  { key: "founderEmail", label: "Founder email" },
];

function renderVal(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

function readFileAsBase64(file: File): Promise<{ base64: string; ext: string; contentType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => {
      const result = reader.result as string;
      const commaIdx = result.indexOf(",");
      const base64 = commaIdx >= 0 ? result.slice(commaIdx + 1) : result;
      const ext = (file.name.split(".").pop() ?? "png").toLowerCase();
      resolve({ base64, ext, contentType: file.type || "image/png" });
    };
    reader.readAsDataURL(file);
  });
}

function DeadlineBadge({
  paidAt,
  createdAt,
  completed,
}: {
  paidAt: string | null;
  createdAt: string | null;
  completed: boolean;
}) {
  const base = paidAt ?? createdAt;
  if (!base) return null;
  const deadline = new Date(base).getTime() + 48 * 60 * 60 * 1000;
  const now = Date.now();
  const overdue = !completed && now > deadline;
  const ms = deadline - now;
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  const label = completed
    ? "Completed"
    : overdue
      ? `Overdue by ${h}h ${m}m`
      : `Due in ${h}h ${m}m`;
  const tone = completed
    ? "bg-[#DDF0DA] text-[#2E5B2E]"
    : overdue
      ? "bg-red/10 text-red border border-red/30"
      : "bg-accent-soft text-accent-ink";
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium ${tone}`}>
      {label}
    </span>
  );
}

function TaskDetail() {
  const { submissionId } = Route.useParams();
  const runDetail = useServerFn(getTaskDetail);
  const runSetStatus = useServerFn(setTargetStatus);
  const runAdd = useServerFn(addTarget);
  const runRemove = useServerFn(removeTarget);
  const runFulfillment = useServerFn(setFulfillment);
  const runGetOnePagerPdf = useServerFn(getOnePagerPdf);
  const runUploadProof = useServerFn(uploadTargetProof);
  const runGetProofUrl = useServerFn(getTargetProofUrl);
  const runComplete = useServerFn(completeTask);
  const runGetReportUrl = useServerFn(getCompletionReportUrl);
  const runResendCompletion = useServerFn(resendCompletionEmail);

  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [addProgramId, setAddProgramId] = useState<string>("");
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState("one-pager.pdf");
  const [preparing, setPreparing] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const refresh = useCallback(async () => {
    const d = await runDetail({ data: { submissionId } });
    setData(d);
    const r: Record<string, string> = {};
    const n: Record<string, string> = {};
    for (const t of d.targets) {
      r[t.id] = t.match_reason ?? "";
      n[t.id] = t.note ?? "";
    }
    setReasons(r);
    setNotes(n);
  }, [runDetail, submissionId]);

  useEffect(() => {
    (async () => {
      try {
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  async function withBusy(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function prepareOnePager() {
    if (pdfUrl || preparing) return;
    setPreparing(true);
    try {
      const res = await runGetOnePagerPdf({ data: { submissionId } });
      if (!res.ok) {
        alert(
          res.reason === "no_content"
            ? "One-pager not available yet."
            : "Could not prepare one-pager",
        );
        return;
      }
      setPdfName(res.fileName);
      setPdfUrl(res.url);
    } catch (err) {
      console.error("one-pager pdf failed", err);
      alert("Could not prepare one-pager");
    } finally {
      setPreparing(false);
    }
  }

  async function handleProofUpload(t: TargetRow, file: File) {
    setUploadingId(t.id);
    try {
      const { base64, ext, contentType } = await readFileAsBase64(file);
      await runUploadProof({ data: { targetId: t.id, base64, ext, contentType } });
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingId(null);
    }
  }

  async function viewProof(t: TargetRow) {
    try {
      const res = await runGetProofUrl({ data: { targetId: t.id } });
      if (res.ok) window.open(res.url, "_blank", "noopener");
      else alert("Proof not available");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to open proof");
    }
  }

  async function handleComplete() {
    if (!data) return;
    if (!confirm("Mark task complete? This will email the founder a completion report.")) return;
    setCompleting(true);
    try {
      const res = await runComplete({ data: { submissionId } });
      await refresh();
      alert(
        res.emailed
          ? "Task completed and founder emailed the report."
          : "Task completed. Founder email could not be sent (check logs).",
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to complete");
    } finally {
      setCompleting(false);
    }
  }

  async function handleDownloadReport() {
    setDownloadingReport(true);
    try {
      const res = await runGetReportUrl({ data: { submissionId } });
      if (res.ok && res.url) window.open(res.url, "_blank", "noopener");
      else alert("Completion report not available");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to download report");
    } finally {
      setDownloadingReport(false);
    }
  }

  async function handleResendCompletion() {
    if (!confirm("Resend the completion email to the founder?")) return;
    setResendingEmail(true);
    try {
      const res = await runResendCompletion({ data: { submissionId } });
      if (res.emailed) {
        await refresh();
        alert("Completion email resent.");
      } else {
        alert(res.error ?? "Failed to send email");
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to resend email");
    } finally {
      setResendingEmail(false);
    }
  }

  function relativeTime(iso: string | null): string {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  }


  if (loading || !data) {
    return <p className="text-ink-tertiary">Loading…</p>;
  }

  const form = data.submission.form as FormShape;
  const company =
    renderVal((form as Record<string, unknown> | null)?.company) ??
    data.deck_file_name?.replace(/\.pdf$/i, "") ??
    "Task";

  const completed = data.submission.fulfillment_status === "completed";
  const totalTargets = data.targets.length;
  const pendingCount = data.targets.filter((t) => t.status === "pending").length;
  const canComplete = !completed && totalTargets > 0 && pendingCount === 0;

  return (
    <>
      <div className="mb-6 flex items-center justify-between gap-3">
        <Link
          to="/tasks"
          className="inline-flex items-center gap-1 text-[13px] text-ink-secondary hover:text-ink"
        >
          <ChevronLeft className="w-4 h-4" /> Back to tasks
        </Link>
        <DeadlineBadge
          paidAt={data.submission.paid_at}
          createdAt={data.submission.created_at}
          completed={completed}
        />
      </div>

      <ScoreHeader
        eyebrow="paid submission"
        title={company}
        score={data.score}
        actions={
          <>
            {data.deck_pdf_url ? (
              <a
                href={data.deck_pdf_url}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-ink px-4 py-2 text-[13.5px] font-semibold hover:-translate-y-px transition"
              >
                <ExternalLink className="w-4 h-4" /> View original deck (PDF)
              </a>
            ) : null}
            {data.one_pager_content ? (
              pdfUrl ? (
                <a
                  href={pdfUrl}
                  download={pdfName}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-[13.5px] hover:border-ink"
                >
                  <Download className="w-4 h-4" /> Download one-pager
                </a>
              ) : (
                <button
                  type="button"
                  onClick={prepareOnePager}
                  disabled={preparing}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-[13.5px] hover:border-ink disabled:opacity-60"
                >
                  <Download className="w-4 h-4" />
                  {preparing ? "Preparing…" : "Download one-pager"}
                </button>
              )
            ) : (
              <span className="inline-flex items-center rounded-full border border-line px-4 py-2 text-[13px] text-ink-tertiary">
                One-pager preparing…
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {!completed ? (
                <>
                  <span className="text-[12px] text-ink-tertiary">Status</span>
                  <Select
                    value={
                      data.submission.fulfillment_status === "unassigned"
                        ? "assigned"
                        : data.submission.fulfillment_status
                    }
                    onValueChange={(v) =>
                      withBusy(() =>
                        runFulfillment({
                          data: {
                            submissionId,
                            status: v as "assigned" | "in_progress",
                          },
                        }).then(() => undefined),
                      )
                    }
                    disabled={busy || completing}
                  >
                    <SelectTrigger className="h-8 text-[13px] min-w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleComplete}
                    disabled={!canComplete || completing}
                    className="h-8 gap-1.5"
                    title={
                      !canComplete
                        ? totalTargets === 0
                          ? "Add at least one program first"
                          : "Set a status on all programs first"
                        : undefined
                    }
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {completing ? "Completing…" : "Complete"}
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DDF0DA] text-[#2E5B2E] px-3 py-1 text-[12.5px] font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> Completed
                  </span>
                  {data.submission.completion_report_pdf_path ? (
                    <button
                      type="button"
                      onClick={handleDownloadReport}
                      disabled={downloadingReport}
                      className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-ink px-3 py-1.5 text-[12.5px] font-semibold hover:-translate-y-px transition disabled:opacity-60"
                    >
                      <Download className="w-4 h-4" />
                      {downloadingReport ? "Preparing…" : "Download completion report"}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleResendCompletion}
                    disabled={resendingEmail}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-[12.5px] hover:border-ink disabled:opacity-60"
                  >
                    {resendingEmail ? "Sending…" : "Resend completion email"}
                  </button>
                  {data.submission.completion_emailed_at ? (
                    <span className="text-[12px] text-ink-tertiary">
                      Last emailed: {relativeTime(data.submission.completion_emailed_at)}
                    </span>
                  ) : (
                    <span className="text-[12px] text-ink-tertiary">Not yet emailed</span>
                  )}
                </div>
              )}
            </div>
          </>
        }
      />

      {!completed && totalTargets > 0 && pendingCount > 0 ? (
        <div className="mb-6 rounded-2xl border border-line bg-surface-sunken/50 px-4 py-3 text-[13px] text-ink-secondary">
          {pendingCount} program{pendingCount === 1 ? "" : "s"} still Pending. Set every program to Submitted or Not a fit to complete this task.
        </div>
      ) : null}

      {data.score ? (
        <div className="mb-6">
          <DeckAnalysisView score={data.score} />
        </div>
      ) : (
        <div className="mb-6 bg-surface border border-line rounded-2xl p-6 text-ink-tertiary text-[14px]">
          No analysis available for this deck yet.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        {/* LEFT — application details */}
        <section className="bg-surface border border-line rounded-2xl p-6">
          <h2 className="font-display text-[18px] font-semibold">Application</h2>
          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[14px]">
            {FORM_FIELDS.map(({ key, label }) => {
              const val = renderVal((form as Record<string, unknown> | null)?.[key]);
              if (val === null) return null;
              return (
                <div key={key}>
                  <dt className="text-ink-tertiary text-[12px] uppercase tracking-wider">
                    {label}
                  </dt>
                  <dd className="mt-0.5 text-ink whitespace-pre-wrap">{val}</dd>
                </div>
              );
            })}
          </dl>
        </section>

        {/* RIGHT — programs */}
        <section className="bg-surface border border-line rounded-2xl p-6 lg:sticky lg:top-6 self-start">
          <h2 className="font-display text-[18px] font-semibold">Programs</h2>
          <div className="mt-4 space-y-4">
            {data.targets.length === 0 ? (
              <p className="text-ink-tertiary text-[13px]">No programs yet.</p>
            ) : (
              data.targets.map((t) => (
                <div key={t.id} className="border border-line rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-[14px]">{t.program_name}</p>
                      {t.program_type ? (
                        <p className="text-[11px] text-ink-tertiary uppercase tracking-wider">
                          {t.program_type}
                        </p>
                      ) : null}
                    </div>
                    <button
                      onClick={() =>
                        withBusy(() =>
                          runRemove({ data: { targetId: t.id } }).then(() => undefined),
                        )
                      }
                      disabled={busy || completed}
                      className="text-[12px] text-ink-tertiary hover:text-ink disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="mt-2">
                    <Select
                      value={t.status}
                      onValueChange={(v) =>
                        withBusy(() =>
                          runSetStatus({
                            data: {
                              targetId: t.id,
                              status: v as "pending" | "submitted" | "not_a_fit",
                            },
                          }).then(() => undefined),
                        )
                      }
                      disabled={busy || completed}
                    >
                      <SelectTrigger className="h-8 text-[13px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="submitted">Submitted</SelectItem>
                        <SelectItem value="not_a_fit">Not a fit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Note (short) */}
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={notes[t.id] ?? ""}
                      onChange={(e) =>
                        setNotes((r) => ({ ...r, [t.id]: e.target.value }))
                      }
                      placeholder="Note (optional)"
                      className="h-8 text-[13px]"
                      disabled={completed}
                    />
                    <Button
                      variant="secondary"
                      onClick={() =>
                        withBusy(() =>
                          runSetStatus({
                            data: {
                              targetId: t.id,
                              status: t.status,
                              note: notes[t.id] ?? null,
                            },
                          }).then(() => undefined),
                        )
                      }
                      disabled={
                        busy || completed || (notes[t.id] ?? "") === (t.note ?? "")
                      }
                      className="h-8 text-[12px]"
                    >
                      Save
                    </Button>
                  </div>

                  {/* Match reason (existing) */}
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={reasons[t.id] ?? ""}
                      onChange={(e) =>
                        setReasons((r) => ({ ...r, [t.id]: e.target.value }))
                      }
                      placeholder="Match reason (optional)"
                      className="h-8 text-[13px]"
                      disabled={completed}
                    />
                    <Button
                      variant="secondary"
                      onClick={() =>
                        withBusy(() =>
                          runSetStatus({
                            data: {
                              targetId: t.id,
                              status: t.status,
                              match_reason: reasons[t.id] ?? null,
                            },
                          }).then(() => undefined),
                        )
                      }
                      disabled={
                        busy ||
                        completed ||
                        (reasons[t.id] ?? "") === (t.match_reason ?? "")
                      }
                      className="h-8 text-[12px]"
                    >
                      Save
                    </Button>
                  </div>

                  {/* Proof screenshot (optional) */}
                  <div className="mt-2 flex items-center gap-3 text-[12px]">
                    <input
                      ref={(el) => {
                        fileInputs.current[t.id] = el;
                      }}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleProofUpload(t, f);
                        e.target.value = "";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputs.current[t.id]?.click()}
                      disabled={uploadingId === t.id || completed}
                      className="inline-flex items-center gap-1 text-ink-secondary hover:text-ink disabled:opacity-40"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploadingId === t.id
                        ? "Uploading…"
                        : t.proof_screenshot_path
                          ? "Replace proof"
                          : "Upload proof"}
                    </button>
                    {t.proof_screenshot_path ? (
                      <button
                        type="button"
                        onClick={() => viewProof(t)}
                        className="inline-flex items-center gap-1 text-accent-ink hover:underline"
                      >
                        <Eye className="w-3.5 h-3.5" /> View proof
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>

          {!completed ? (
            <div className="mt-6 border-t border-line pt-4">
              <p className="text-[12px] text-ink-tertiary uppercase tracking-wider mb-2">
                Add program
              </p>
              <div className="flex gap-2">
                <Select value={addProgramId} onValueChange={setAddProgramId} disabled={busy}>
                  <SelectTrigger className="h-9 text-[13px]">
                    <SelectValue placeholder="Select a program…" />
                  </SelectTrigger>
                  <SelectContent>
                    {data.available_programs.length === 0 ? (
                      <div className="px-3 py-2 text-[12px] text-ink-tertiary">
                        No available programs
                      </div>
                    ) : (
                      data.available_programs.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                          {p.type ? ` · ${p.type}` : ""}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button
                  onClick={() =>
                    withBusy(async () => {
                      if (!addProgramId) return;
                      await runAdd({
                        data: { submissionId, programId: addProgramId },
                      });
                      setAddProgramId("");
                    })
                  }
                  disabled={busy || !addProgramId}
                  className="h-9"
                >
                  Add
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
}
